import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageLoading } from '@/components/ui/loading';
import { formatWorkedHours, workerTotal, workedMinutes } from '@/lib/actual-labour';
import { normalizeRateBasis, type RateBasis } from '@shared/labour-costing';
import { validateTimeRange } from '@/lib/validation';
import { useStandardWorkDayHours } from '@/hooks/useStandardWorkDayHours';
import { ArrowLeft, Send } from 'lucide-react';
import type { LabourCategory } from '@/lib/types';

interface CorrectionFields {
  workerCategoryId: string;
  rate: string;
  rateBasis: RateBasis;
  timeIn: string;
  timeOut: string;
}

function unitStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Pending';
  if (status === 'PENDING_CLUSTER') return 'Pending Cluster Head';
  if (status === 'WAITING_FOR_PM') return 'Waiting for Project Manager';
  if (status === 'NOT_REQUIRED') return 'Not required';
  return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (ch: string) => ch.toUpperCase());
}

export default function ActualStatusCorrectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: record, isLoading } = useQuery({
    queryKey: ['actual-status', id],
    queryFn: async () => {
      const res = await api.get<{ data: any }>(`/actual-status/${id}`);
      return res.data;
    },
    enabled: Boolean(id),
  });
  const { data: labourCategories } = useQuery({
    queryKey: ['labour-categories-active'],
    queryFn: async () => {
      const res = await api.get<any>('/labour-categories/all');
      return (res.data ?? []) as LabourCategory[];
    },
  });

  if (isLoading || !record) return <PageLoading />;

  const hasRejectedUnits = (record.workers ?? []).some((worker: any) =>
    (worker.workerUnits ?? []).some((unit: any) => unit.overallStatus === 'REJECTED')
  );
  const isCorrectableStatus = record.status !== 'DRAFT' && record.status !== 'APPROVED';
  if (!isCorrectableStatus || !hasRejectedUnits) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-heading font-bold text-primary">Correct Rejected Entries</h1>
        <Card><CardContent className="py-8 text-center text-sm text-secondary">This record has no rejected worker entries to correct.</CardContent></Card>
        <Button variant="outline" onClick={() => navigate(`/actuals/${record.id}`)}><ArrowLeft size={16} className="mr-1" />Back to record</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/actuals/${record.id}`)}><ArrowLeft size={16} /></Button>
        <div className="min-w-0">
          <h1 className="text-2xl font-heading font-bold text-primary">Correct Rejected Entries</h1>
          <p className="text-sm text-secondary break-words">{record.actual_number} &middot; {record.project_name} &middot; {record.block_name} &middot; {record.work_date}</p>
        </div>
      </div>
      <CorrectionForm key={record.id} record={record} labourCategories={labourCategories ?? []} />
    </div>
  );
}

function CorrectionForm({ record, labourCategories }: { record: any; labourCategories: LabourCategory[] }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const standardHours = useStandardWorkDayHours();
  const [formError, setFormError] = useState('');

  const activityNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const activity of record.activities ?? []) map.set(activity.id, activity.activity_name || 'Activity');
    return map;
  }, [record.activities]);

  const allUnits = useMemo(() => {
    const rows: Array<{ worker: any; unit: any; key: string }> = [];
    for (const worker of record.workers ?? []) {
      for (const unit of worker.workerUnits ?? []) {
        rows.push({ worker, unit, key: `${worker.id}:${unit.unitIndex}` });
      }
    }
    return rows;
  }, [record.workers]);

  const rejectedUnits = useMemo(
    () => allUnits.filter(({ unit }) => unit.overallStatus === 'REJECTED'),
    [allUnits],
  );
  const readOnlyUnits = useMemo(
    () => allUnits.filter(({ unit }) => unit.overallStatus !== 'REJECTED'),
    [allUnits],
  );

  const [fields, setFields] = useState<Record<string, CorrectionFields>>(() => {
    const initial: Record<string, CorrectionFields> = {};
    for (const { worker, unit, key } of rejectedUnits) {
      initial[key] = {
        workerCategoryId: worker.worker_category_id || '',
        rate: String(worker.rate ?? 0),
        rateBasis: normalizeRateBasis(worker.rate_basis ?? worker.rateBasis ?? 'DAILY'),
        timeIn: worker.time_in || '',
        timeOut: worker.time_out || '',
      };
    }
    return initial;
  });

  const updateField = (key: string, patch: Partial<CorrectionFields>) => {
    setFields((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
    setFormError('');
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const entries = rejectedUnits.map(({ worker, unit, key }) => {
        const value = fields[key];
        return {
          workerId: worker.id,
          unitIndex: unit.unitIndex,
          workerCategoryId: value.workerCategoryId,
          rate: Number(value.rate),
          rateBasis: value.rateBasis,
          timeIn: value.timeIn,
          timeOut: value.timeOut,
        };
      });
      const res = await api.put<any>(`/actual-status/${record.id}/rejected-entries`, { entries });
      return res.data;
    },
    onSuccess: () => {
      addToast('Corrected entries resubmitted for approval', 'success');
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      navigate(`/actuals/${record.id}`);
    },
    onError: (err: any) => setFormError(err?.message || 'Unable to resubmit the corrected entries'),
  });

  const submit = () => {
    setFormError('');
    if (rejectedUnits.length === 0) {
      setFormError('There are no rejected worker entries to correct.');
      return;
    }
    for (const { key } of rejectedUnits) {
      const value = fields[key];
      if (!value.workerCategoryId) {
        setFormError('Select a worker category for every rejected entry.');
        return;
      }
      if (!Number.isFinite(Number(value.rate)) || Number(value.rate) < 0) {
        setFormError('Enter a valid rate for every rejected entry.');
        return;
      }
      if (!value.timeIn || !value.timeOut) {
        setFormError('Enter Time In and Time Out for every rejected entry.');
        return;
      }
      const range = validateTimeRange(value.timeIn, value.timeOut, { startLabel: 'Time In', endLabel: 'Time Out' });
      if (!range.valid) {
        setFormError(range.startError || range.endError || 'Enter a valid time range for every rejected entry.');
        return;
      }
    }
    mutation.mutate();
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">Rejected Worker Entries</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-secondary">Only the entries rejected by the approver are editable. Approved and pending entries remain unchanged.</p>
          {rejectedUnits.length === 0 ? (
            <p className="text-sm text-error">No rejected worker entries were found for this record.</p>
          ) : rejectedUnits.map(({ worker, unit, key }) => {
            const value = fields[key];
            const minutes = workedMinutes(value.timeIn, value.timeOut);
            const total = minutes === null ? null : workerTotal(1, minutes, Number(value.rate) || 0, value.rateBasis, standardHours);
            const activityName = activityNames.get(worker.activity_id) || 'Activity';
            const managerReason = unit.managerDecision === 'REJECTED'
              ? `Project Manager${unit.managerDecidedByName ? ` (${unit.managerDecidedByName})` : ''}: ${unit.managerRemarks || 'No reason recorded'}`
              : '';
            const clusterReason = unit.clusterDecision === 'REJECTED'
              ? `Cluster Head${unit.clusterDecidedByName ? ` (${unit.clusterDecidedByName})` : ''}: ${unit.clusterRemarks || 'No reason recorded'}`
              : '';
            return (
              <div key={key} className="rounded-lg border border-error/40 p-4 space-y-4 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-primary break-words">{activityName} &middot; {worker.category_name}</p>
                    <p className="text-xs text-secondary">Worker entry {unit.unitIndex + 1}</p>
                  </div>
                  <span className="rounded-full bg-error/10 px-2 py-1 text-xs font-medium text-error">Rejected</span>
                </div>
                {managerReason && <p className="text-sm text-error break-words">{managerReason}</p>}
                {clusterReason && <p className="text-sm text-error break-words">{clusterReason}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
                  <div className="sm:col-span-2 min-w-0">
                    <Label>Worker Category *</Label>
                    <Select value={value.workerCategoryId} onValueChange={(next) => updateField(key, { workerCategoryId: next })}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {labourCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>{category.name} — {(category.skill_level || category.skillLevel || 'UNCLASSIFIED').replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <Label>Rate (₹/{value.rateBasis === 'HOURLY' ? 'hr' : 'day'}) *</Label>
                    <Input type="number" min={0} step="0.01" value={value.rate} onChange={(event) => updateField(key, { rate: event.target.value })} />
                  </div>
                  <div className="min-w-0">
                    <Label>Rate Basis</Label>
                    <Select value={value.rateBasis} onValueChange={(next) => updateField(key, { rateBasis: next as RateBasis })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAILY">Daily</SelectItem>
                        <SelectItem value="HOURLY">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <Label>Time In *</Label>
                    <Input type="time" value={value.timeIn} onChange={(event) => updateField(key, { timeIn: event.target.value })} />
                  </div>
                  <div className="min-w-0">
                    <Label>Time Out *</Label>
                    <Input type="time" value={value.timeOut} onChange={(event) => updateField(key, { timeOut: event.target.value })} />
                  </div>
                  <div className="min-w-0">
                    <Label>Worked Hours</Label>
                    <div className="h-10 flex items-center text-sm font-medium">{minutes === null ? '—' : formatWorkedHours(minutes)}</div>
                  </div>
                  <div className="min-w-0">
                    <Label>Total</Label>
                    <div className="h-10 flex items-center text-sm font-medium">{total === null ? '—' : `₹${total.toLocaleString('en-IN')}`}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {readOnlyUnits.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Other Worker Entries (Read-only)</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y divide-border rounded-lg border border-subtle">
              {readOnlyUnits.map(({ worker, unit, key }) => (
                <div key={key} className="p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium break-words">{activityNames.get(worker.activity_id) || 'Activity'} &middot; {worker.category_name} &middot; Entry {unit.unitIndex + 1}</span>
                    <span className="text-xs text-secondary">{unitStatusLabel(unit.overallStatus)}</span>
                  </div>
                  <p className="text-xs text-secondary mt-1">Rate ₹{worker.rate} &middot; {worker.time_in || '—'} to {worker.time_out || '—'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {formError && <p className="text-sm text-error" role="alert">{formError}</p>}

      <div className="flex flex-wrap justify-end gap-3">
        <Link to={`/actuals/${record.id}`}><Button type="button" variant="outline">Cancel</Button></Link>
        <Button type="button" onClick={submit} disabled={mutation.isPending || rejectedUnits.length === 0}>
          {!mutation.isPending && <Send size={16} className="mr-1" />}
          {mutation.isPending ? 'Resubmitting...' : 'Resubmit Corrected Entries'}
        </Button>
      </div>
    </>
  );
}
